import { BaseAPIService } from '../api/base';
import { supabase } from '../api/client';

/**
 * MBE (Multistate Bar Examination) Service
 * Handles all MBE exam-related API calls
 */
class MBEService extends BaseAPIService {
  constructor() {
    super('mbe_questions');
  }

  /**
   * Get all MBE questions
   */
  async getQuestions(options = {}) {
    try {
      let query = supabase
        .from(options.useBasicTable ? 'mbe_questions' : 'mbe_questions_readable')
        .select(options.select || '*');

      // Filter by subject
      if (options.subjectId) {
        query = query.eq('subject_id', options.subjectId);
      }

      // Filter by set
      if (options.setId) {
        query = query.eq('set_id', options.setId);
      }

      // Filter by multiple set IDs
      if (options.setIds && Array.isArray(options.setIds)) {
        query = query.in('set_id', options.setIds);
      }

      // Apply ordering
      if (options.orderBy) {
        query = query.order(options.orderBy.column || 'question_number', {
          ascending: options.orderBy.ascending !== false
        });
      } else {
        query = query.order('question_number', { ascending: true });
      }

      // Apply limit
      if (options.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get random questions from multiple sets
   */
  async getRandomQuestionsFromSets(setIds, totalCount) {
    try {
      // Get total count for each set
      const countPromises = setIds.map(async (setId) => {
        const { count, error } = await supabase
          .from('mbe_questions')
          .select('*', { count: 'exact', head: true })
          .eq('set_id', setId);

        if (error) throw error;
        return { setId, count };
      });

      const setCounts = await Promise.all(countPromises);
      const perSetCount = Math.ceil(totalCount / setIds.length);

      // Fetch random questions from each set
      const questionPromises = setIds.map(async (setId) => {
        const setInfo = setCounts.find(s => s.setId === setId);
        const maxQuestions = Math.min(perSetCount, setInfo?.count || 0);

        if (maxQuestions === 0) return [];

        // Random offset for this set
        const offset = Math.floor(Math.random() * Math.max(0, (setInfo?.count || 0) - maxQuestions));

        const { data, error } = await supabase
          .from('mbe_questions')
          .select('*')
          .eq('set_id', setId)
          .range(offset, offset + maxQuestions - 1);

        if (error) throw error;
        return data || [];
      });

      const allQuestions = await Promise.all(questionPromises);
      const flatQuestions = allQuestions.flat();

      // Shuffle and trim to exact count
      const shuffled = flatQuestions.sort(() => Math.random() - 0.5);
      const finalQuestions = shuffled.slice(0, totalCount);

      return { success: true, data: finalQuestions };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get question by ID
   */
  async getQuestionById(id) {
    try {
      const { data, error } = await supabase
        .from('mbe_questions_readable')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Create new MBE question
   */
  async createQuestion(questionData) {
    return this.create(questionData);
  }

  /**
   * Update MBE question
   */
  async updateQuestion(id, updates) {
    return this.update(id, updates);
  }

  /**
   * Delete MBE question
   */
  async deleteQuestion(id) {
    return this.delete(id);
  }

  /**
   * Bulk upload questions
   */
  async bulkUploadQuestions(questions) {
    return this.createMany(questions);
  }

  /**
   * Get MBE sets
   */
  async getSets() {
    try {
      const { data, error } = await supabase
        .from('mbe_sets')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Save or update user answer with set_id and subject_id
   */
  async saveAnswerWithSetAndSubject(userId, questionId, answer, isCorrect, setId, subjectId) {
    // In Study mode, answers are saved in mbe_user_answers_drafts table via saveDraftToServer
    // This function is called but doesn't need to save to mbe_exam_results table
    // Just return success to avoid errors
    return { success: true, data: { message: 'Answer saved via draft system' } };
  }

  /**
   * Save or update user answer with subject_id (deprecated - use saveAnswerWithSetAndSubject)
   */
  async saveAnswerWithSubject(userId, questionId, answer, isCorrect, subjectId) {
    try {
      // Check if answer already exists
      const { data: existing, error: checkError } = await supabase
        .from('mbe_exam_results')
        .select('*')
        .eq('user_id', userId)
        .eq('question_id', questionId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows
        throw checkError;
      }

      let result;
      if (existing) {
        // Update existing answer
        result = await supabase
          .from('mbe_exam_results')
          .update({
            answers: answer,  // 컬럼명은 answers
            is_correct: isCorrect,
            subject_id: subjectId,
            answered_at: new Date().toISOString(),
          })
          .eq('user_id', userId)
          .eq('question_id', questionId)
          .select()
          .single();
      } else {
        // Insert new answer
        result = await supabase
          .from('mbe_exam_results')
          .insert([
            {
              user_id: userId,
              question_id: questionId,
              answers: answer,  // 컬럼명은 answers
              is_correct: isCorrect,
              subject_id: subjectId,
              answered_at: new Date().toISOString(),
            },
          ])
          .select()
          .single();
      }

      const { data, error } = result;
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Save or update user answer (legacy - without subject_id)
   */
  async saveAnswer(userId, questionId, answer, isCorrect) {
    try {
      // Check if answer already exists
      const { data: existing, error: checkError } = await supabase
        .from('mbe_exam_results')
        .select('*')
        .eq('user_id', userId)
        .eq('question_id', questionId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows
        throw checkError;
      }

      let result;
      if (existing) {
        // Update existing answer
        result = await supabase
          .from('mbe_exam_results')
          .update({
            answers: answer,  // 컬럼명은 answers
            is_correct: isCorrect,
            answered_at: new Date().toISOString(),
          })
          .eq('user_id', userId)
          .eq('question_id', questionId)
          .select()
          .single();
      } else {
        // Insert new answer
        result = await supabase
          .from('mbe_exam_results')
          .insert([
            {
              user_id: userId,
              question_id: questionId,
              answers: answer,  // 컬럼명은 answers
              is_correct: isCorrect,
              answered_at: new Date().toISOString(),
            },
          ])
          .select()
          .single();
      }

      const { data, error } = result;
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Save test results
   */
  async saveTestResults(testData) {
    try {
      const { data, error } = await supabase
        .from('mbe_exam_results')
        .insert(testData);

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user progress
   */
  async getUserProgress(userId, setId = null) {
    try {
      let query = supabase
        .from('mbe_exam_results')
        .select('*, mbe_questions!inner(*)')
        .eq('user_id', userId);

      if (setId) {
        query = query.eq('mbe_questions.set_id', setId);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Calculate statistics
      const stats = {
        totalQuestions: data.length,
        correctAnswers: data.filter(a => a.is_correct).length,
        wrongAnswers: data.filter(a => !a.is_correct).length,
        accuracy: 0,
      };

      if (stats.totalQuestions > 0) {
        stats.accuracy = (stats.correctAnswers / stats.totalQuestions) * 100;
      }

      return { success: true, data: { answers: data, stats } };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user answers by set IDs
   */
  async getUserAnswersBySetIds(userId, setIds) {
    try {

      // First, let's check what's in the database
      // First, let's check what's in the database
      await supabase
        .from('mbe_exam_results')
        .select('set_id, question_id')
        .eq('user_id', userId)
        .limit(10);


      // Get all answers for these set IDs
      const { data, error } = await supabase
        .from('mbe_exam_results')
        .select('question_id, answers, is_correct, set_id')  // 컬럼명은 answers
        .eq('user_id', userId)
        .in('set_id', setIds);

      if (error) throw error;


      // Convert to object for easy lookup
      const answersMap = {};
      if (data) {
        data.forEach(answer => {
          answersMap[answer.question_id] = answer.answers;  // 컬럼명은 answers
        });
      }


      return { success: true, data: answersMap };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get sets by subject ID
   */
  async getSetsBySubject(subjectId) {
    try {

      // Get all unique set_ids for questions with this subject
      const { data: questions, error } = await supabase
        .from('mbe_questions')
        .select('set_id, id')
        .eq('subject_id', subjectId);

      if (error) throw error;


      // Get unique set IDs
      const setIds = [...new Set(questions?.map(q => q.set_id).filter(id => id !== null) || [])];

      return { success: true, data: setIds };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user answers by subject (for Study mode)
   */
  async getUserAnswersBySubject(userId, subjectId) {
    try {

      // First, let's check what's actually in the drafts table
      // First, let's check what's actually in the drafts table
      const { data: allDrafts } = await supabase
        .from('mbe_user_answers_drafts')
        .select('draft_type, target_id, updated_at')
        .eq('user_id', userId)
        .eq('draft_type', 'mbe_study');


      // Study mode saves to mbe_user_answers_drafts table with draft_type='mbe_study'
      // The targetId for study mode is the subjectId
      let { data: studyDraft, error: draftError } = await supabase
        .from('mbe_user_answers_drafts')
        .select('content, updated_at')
        .eq('user_id', userId)
        .eq('draft_type', 'mbe_study')
        .eq('target_id', subjectId)
        .single();

      // Fallback: 잘못 저장된 JSON 형식의 target_id도 확인
      if ((!studyDraft || draftError) && allDrafts && allDrafts.length > 0) {
        for (const draft of allDrafts) {
          try {
            // target_id가 JSON 문자열인 경우 파싱 시도
            if (draft.target_id && draft.target_id.includes('subjectId')) {
              const parsed = JSON.parse(draft.target_id);
              if (parsed.subjectId === subjectId) {
                const { data: malformedDraft } = await supabase
                  .from('mbe_user_answers_drafts')
                  .select('content, updated_at')
                  .eq('user_id', userId)
                  .eq('draft_type', 'mbe_study')
                  .eq('target_id', draft.target_id)
                  .single();
                if (malformedDraft) {
                  studyDraft = malformedDraft;
                  draftError = null;
                  break;
                }
              }
            }
          } catch (e) {
            // JSON 파싱 실패는 무시
          }
        }
      }

      if (draftError) {
        if (draftError.code === 'PGRST116') { // PGRST116 = no rows
        } else {
          console.error('[DEBUG] Draft query error:', draftError);
          throw draftError;
        }
      }

      if (studyDraft) {
        if (studyDraft.content) {
          if (studyDraft.content.userAnswers) {
          }
        }
      }

      // Extract userAnswers from the draft content
      const answersMap = {};

      if (studyDraft && studyDraft.content && studyDraft.content.userAnswers) {
        Object.assign(answersMap, studyDraft.content.userAnswers);
      }


      return { success: true, data: answersMap };
    } catch (error) {
      console.error('[DEBUG] getUserAnswersBySubject error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete user answers by subject
   */
  async deleteUserAnswersBySubject(userId, subjectId) {
    try {
      // Delete the draft for this subject from mbe_user_answers_drafts table
      const { error } = await supabase
        .from('mbe_user_answers_drafts')
        .delete()
        .eq('user_id', userId)
        .eq('draft_type', 'mbe_study')
        .eq('target_id', subjectId);

      if (error) throw error;


      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get subjects
   */
  async getSubjects() {
    try {
      const { data, error } = await supabase
        .from('subjects')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Add to wrong answers
   */
  async addToWrongAnswers(userId, questionId, selectedAnswer) {
    try {
      const { data, error } = await supabase
        .from('wrong_answers')
        .insert([
          {
            user_id: userId,
            question_id: questionId,
            question_type: 'mbe',
            selected_answer: selectedAnswer,
            created_at: new Date().toISOString(),
          },
        ])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

// Export singleton instance
export const mbeService = new MBEService();
export default mbeService;